import { useState, useEffect } from 'react';
import { useIntakeForm } from '../../contexts/intakeForm';
import CBInvoiceItemDetailsPercentage from '../CBInvoiceItemDetailsPercentage';
import CBInvoiceItemDetailsNonPercentage from '../CBInvoiceItemDetailsNonPercentage';

const CostBreakdownAddInvoiceItem = ({
  isPlaster,
  setAddingActive,
  onProductFinish,
  add,
  update,
  deleteRow,
  options = {},
  defaultInvoice,
  editRow,
  setEditRow,
}) => {
  const { materialObjects } = useIntakeForm();
  const [newItem, setNewItem] = useState(defaultInvoice);
  const [disableAdd, setDisableAdd] = useState(false);
  const [productInfo, setProductInfo] = useState({ tierList: {}, allColors: [], colorList: [] });

  const updateState = (e, field) => {
    if (field === 'name' && e.target.value === 'Plaster') {
      setNewItem({
        ...newItem,
        [field]: e.target.value,
        price: options[e.target.value],
        manufacturer: '',
        minIA: 0,
        minPrice: 0,
        tier: 0,
        color: '',
      });
    } else if (field === 'name' && !isPlaster && e.target.value === 'Fuel Surcharge') {
      //automatically designate fuel surcharges on gunite as percentages
      setNewItem({
        ...newItem,
        [field]: e.target.value,
        price: options[e.target.value],
        isPercentage: true,
        amount: 1,
      });
    } else if (field === 'name') {
      setNewItem({
        ...newItem,
        [field]: e.target.value,
        price: options[e.target.value],
        amount: 0,
      });
    } else if (field === 'manufacturer') {
      setNewItem({
        ...newItem,
        [field]: e.target.value,
        tier: 0,
        color: '',
        price: 0,
        minIA: 0,
        minPrice: 0,
      });
      if (e.target.value !== '') {
        const product = collectTiersAndColors(e.target.value.split(' -- '));
        onProductFinish(product?.finishType);
      } else {
        setProductInfo({
          ...productInfo,
          tierList: {},
        });
      }
    } else if (field === 'color') {
      if (e.target.value === '') return;
      const newTier = calculateTierFromColor(e.target.value);
      const tierInfo = handleTierPriceInfo(newTier);
      setNewItem({
        ...newItem,
        ...tierInfo,
        [field]: e.target.value,
        tier: newTier,
      });
    } else {
      setNewItem({
        ...newItem,
        [field]: e.target.value,
      });
    }
  };

  // Grabs the tiers and colors of the selected manufacturer/brand
  const collectTiersAndColors = info => {
    if (!materialObjects) return;
    const manufacturer = materialObjects.find(brand => brand.name === info[0]);
    if (!manufacturer) return;
    const product = manufacturer?.product?.find(product => product.name === info[1]);
    const tierList = {};
    const colors = [];
    for (const tierObj of product.tier) {
      tierList[`Tier ${tierObj.tier}`] = [];
      for (const colorObj of tierObj.color) {
        colors.push(colorObj.name);
        tierList[`Tier ${tierObj.tier}`].push(colorObj.name);
      }
    }
    setProductInfo({
      ...productInfo,
      manufacturer: manufacturer.name,
      product: product.name,
      productId: product.id,
      tierObjects: product.tier,
      tierList: tierList,
      allColors: colors,
      colorList: colors,
    });

    return product;
  };

  const calculateTierFromColor = color => {
    for (const tier of Object.keys(productInfo.tierList)) {
      if (productInfo.tierList[tier].includes(color)) {
        return tier.split(' ')[1];
      }
    }
  };

  const handleTierPriceInfo = tier => {
    const tierInfo = {
      price: 0,
      minIA: 0,
      minPrice: 0,
    };
    for (const obj of productInfo.tierObjects) {
      if (obj.tier === tier) {
        tierInfo.price = obj.pricePerSqFoot;
        tierInfo.minIA = 10;
        tierInfo.minPrice = 20;
      }
    }
    return tierInfo;
  };

  const handleAdd = () => {
    setAddingActive(false);
    if (editRow.row) {
      update(newItem, editRow.i);
    } else {
      add(newItem);
    }

    setEditRow({ row: null, i: null });
  };

  useEffect(() => {
    // if editRow is not null, populate with row values
    if (editRow.row) {
      setNewItem({
        ...editRow.row,
      });
    }
  }, [editRow]);

  //this useEffect blocks clicking the "Add" button if the new item's information is not completely filled out
  useEffect(() => {
    let disabled = false;
    if (
      newItem.name === '' ||
      !newItem.amount ||
      Number(newItem.amount) === 0 ||
      !newItem.price ||
      Number(newItem.price) === 0
    ) {
      disabled = true;
    }
    if (newItem.name === 'Minimum') {
      if (newItem.amountForMinimum === '') {
        disabled = true;
      }
      if (newItem.amount !== 1) {
        updateState({ target: { value: 1 } }, 'amount');
      }
    }
    setDisableAdd(disabled);
  }, [newItem.amount, newItem.name, newItem.price, newItem.amountForMinimum]);

  useEffect(() => {
    if (newItem.manufacturer) {
      collectTiersAndColors(newItem.manufacturer.split(' -- '));
    }
  }, [newItem.manufacturer]);

  return (
    <div className='flex flex-col w-full p-4 mt-4 justify-center rounded border bg-subtle-grey gap-4'>
      {newItem.isPercentage ? (
        <CBInvoiceItemDetailsPercentage
          newItem={newItem}
          options={options}
          updateState={updateState}
        />
      ) : (
        <CBInvoiceItemDetailsNonPercentage
          newItem={newItem}
          options={options}
          isPlaster={isPlaster}
          updateState={updateState}
          productInfo={productInfo}
        />
      )}

      <div className='flex justify-between'>
        <div>
          <button
            className={`border border-navy-light rounded bg-white text-dark-blue py-2 px-3 font-semibold hover:bg-gunite-red hover:text-white ${editRow.row ? '' : 'hidden'}`}
            onClick={() => {
              deleteRow(editRow.i);
              setAddingActive(false);
              setEditRow({ row: null, i: null });
            }}
          >
            Delete
          </button>
        </div>

        <div className='flex justify-end gap-4'>
          <button
            className='border border-navy-light rounded bg-white py-2 px-3 font-semibold text-dark-blue hover:text-white hover:bg-gunite-red'
            onClick={() => {
              setAddingActive(false);
              setEditRow({ row: null, i: null });
            }}
          >
            Cancel
          </button>
          <button
            className={`border border-navy-light rounded py-2 px-4 font-semibold  ${disableAdd ? 'text-opaque-job-details-gray bg-disabled-gray' : 'bg-white text-dark-blue hover:bg-dark-blue hover:text-white'}`}
            disabled={disableAdd}
            onClick={() => {
              handleAdd();
            }}
          >
            {editRow.row ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CostBreakdownAddInvoiceItem;
